import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

// Scanner-safe: the token is burned ONLY by this POST, fired from a real browser
// on mount. Mail-scanner prefetches do a GET, which never consumes the token.
export default function VerifyMagicLink() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const fired = useRef(false); // guard React StrictMode double-invoke
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) { setState("error"); setMsg("This sign-in link is missing its token."); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify", { token });
        applyAuth(data.user ? { ...data.user, token: data.token } : data);
        setState("ok");
        toast.success("Signed in");
        const dest = sessionStorage.getItem("rp_post_login") || "/";
        sessionStorage.removeItem("rp_post_login");
        setTimeout(() => nav(dest), 600);
      } catch (err) {
        // If this device already holds a session, treat as success.
        try {
          const me = await api.get("/auth/me");
          if (me.data && me.data.id) {
            applyAuth(me.data);
            setState("ok");
            setTimeout(() => nav("/"), 400);
            return;
          }
        } catch (_) { /* not signed in */ }
        // Otherwise say WHY (used vs expired vs unknown).
        let why = apiError(err.response?.data?.detail);
        try {
          const st = await api.get(`/auth/link-status?token=${encodeURIComponent(token)}`);
          if (st.data.status === "used") why = "This sign-in link has already been used.";
          else if (st.data.status === "expired") why = `This sign-in link has expired (links last ${st.data.minutes} minutes).`;
          else if (st.data.status === "unknown") why = "This sign-in link is not valid.";
        } catch (_) { /* keep server detail */ }
        setState("error");
        setMsg(why || "This sign-in link could not be used.");
      }
    })();
  }, [token, applyAuth, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0C1017] p-6" data-testid="verify-magic-link-page">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 text-center rp-fade-up">
        <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-14 w-auto object-contain mx-auto mb-6" />
        {state === "verifying" && (
          <div data-testid="verify-loading">
            <Loader2 className="w-10 h-10 animate-spin text-[#C8000F] mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold">Signing you in…</h1>
            <p className="text-slate-500 text-sm mt-2">Hang tight, confirming your link.</p>
          </div>
        )}
        {state === "ok" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-10 h-10 text-[#3B8A2B] mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold">You're in!</h1>
            <p className="text-slate-500 text-sm mt-2">Taking you to Rodeo Pilot…</p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <XCircle className="w-10 h-10 text-[#C8000F] mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold">Link didn't work</h1>
            <p className="text-slate-500 text-sm mt-2 mb-6">{msg}</p>
            <button onClick={() => nav("/login")} data-testid="verify-request-new-btn"
              className="w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-lg">
              Request a new link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
